﻿import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { TABLE_HEADERS } from "@/utils/constants/appConstants/commonConstants";
import { DateFormatter, TimeFormatter } from "@/utils/functions/helperFunctions";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { NO_GROUP_YET, CREATE_YOUR_GROUPS } from "@/utils/constants/messages";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { MobileDateField, MobileLabelValue } from "@/components/cards/MobileDateField";
import { MobileListChevron } from "@/components/cards/MobileListChevron";
import {
    GroupTableSkeletonDesktop,
    GroupTableSkeletonMobile,
} from "@/components/skeletons";

interface GroupTableProps {
    groups: [];
    setGroups: (group: any) => void;
    isLoading: boolean;
    dataLength: number;
    sorting: string;
    allFetched?: boolean;
    setSorting: ({}) => void;
    setPageNo: (pageNo: number) => void;
    pageNo: number;
    dataLoad: boolean;
    id: string;
}

const GroupTable: React.FC<GroupTableProps> = (props) => {
    const [nameStatus, setNameStatus] = React.useState(true);
    const [createdAtStatus, setCreatedAtStatus] = React.useState(true);
    const [sortElement, setSortElement] = React.useState("created_at");
    const navigate = useNavigate();
    const isCompactLayout = useCompactListLayout();

    const nameSort = () => {
        setNameStatus(!nameStatus);
        setSortElement("name");
        props.setPageNo(1);
        props.setSorting({ name: !nameStatus ? 1 : -1 });
    };

    const createdAtSort = () => {
        setCreatedAtStatus((prevStatus) => !prevStatus);
        setSortElement("created_at");
        props.setPageNo(1);
        props.setSorting({ created_at: createdAtStatus ? 1 : -1 });
    };

    const groups = Array.isArray(props.groups) ? props.groups : [];
    const isPendingInitialLoad = props.dataLoad || (!groups.length && props.isLoading);
    const isLoadingMore = props.isLoading && !props.dataLoad;

    if (isCompactLayout) {
        if (isPendingInitialLoad) {
            return (
                <div className="gs-users-table-wrap">
                    <GroupTableSkeletonMobile />
                </div>
            );
        }

        if (!groups.length) {
            return (
                <NoDataAvailable>
                    <h4 className="text-center" id={`${props.id}-no-data-mb`}>{NO_GROUP_YET}</h4>
                    <p className="text-center" id={`${props.id}-no-data-message-mb`}>{CREATE_YOUR_GROUPS}</p>
                </NoDataAvailable>
            );
        }

        return (
            <div className="gs-users-table-wrap">
                {groups.map((group: any, key: number) => (
                    <div className="gs-users-mobile-card gs-mobile-list-card" key={group.id ?? key}>
                        <div className="gs-mobile-card-icon">
                            <em className="icon ni ni-users" id={`${props.id}-icon-${key}-mb`} />
                        </div>
                        <div className="gs-users-mobile-meta">
                            <p className="gs-users-table-user-email" id={`${props.id}-name-${key}-mb`}>
                                {group?.name}
                            </p>
                            <MobileLabelValue
                                label="Roles: "
                                value={group?.roles.length}
                                id={`${props.id}-role-count-${key}-mb`}
                            />
                            <MobileLabelValue
                                label="Users: "
                                value={group?.user_count}
                                id={`${props.id}-user-count-${key}-mb`}
                            />
                            <MobileDateField
                                label="Created At: "
                                value={group.created_at}
                                dateId={`${props.id}-created-at-date-${key}-mb`}
                                timeId={`${props.id}-created-at-time-${key}-mb`}
                            />
                        </div>
                        <MobileListChevron
                            id={`${props.id}-redirect-${key}-mb`}
                            onClick={() => navigate(`/group/${group.id}`)}
                        />
                    </div>
                ))}
                {isLoadingMore ? <GroupTableSkeletonMobile rowCount={2} /> : null}
            </div>
        );
    }

    return (
        <div className="gs-users-table-wrap">
            <table
                className="nk-tb-list nk-tb-ulist table-weight"
                style={{ tableLayout: "fixed", width: "100%" }}
            >
                <colgroup>
                    <col className="gs-nk-w-40" />
                    <col className="gs-nk-w-20" />
                    <col className="gs-nk-w-20" />
                    <col className="gs-nk-w-20" />
                </colgroup>
                <thead>
                    <tr className="nk-tb-item nk-tb-head">
                        <th className="nk-tb-col gs-nk-w-40 cursor-pointer" onClick={nameSort}>
                            <span id={`${props.id}-name-th`}>{TABLE_HEADERS.NAME}</span>
                            <em
                                id={`${props.id}-name-th-sort-icon`}
                                className={`ml-1 icon ni ${sortElement === "name" ? (nameStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                            />
                        </th>
                        <th className="nk-tb-col gs-nk-w-20">
                            <span id={`${props.id}-role-th`}>{TABLE_HEADERS.ROLE}</span>
                        </th>
                        <th className="nk-tb-col gs-nk-w-20">
                            <span id={`${props.id}-user-th`}>{TABLE_HEADERS.USER}</span>
                        </th>
                        <th className="nk-tb-col gs-nk-w-20 cursor-pointer" onClick={createdAtSort}>
                            <span id={`${props.id}-created-at-th`}>{TABLE_HEADERS.CREATED_AT}</span>
                            <em
                                id={`${props.id}-created-at-th-sort-icon`}
                                className={`ml-1 icon ni ${sortElement === "created_at" ? (createdAtStatus ? "ni-upword-alt-fill" : "ni-downward-alt-fill") : ""}`}
                            />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {!isPendingInitialLoad ? (
                        groups.length > 0 ? (
                            <>
                                {groups.map((group: any, key: number) => (
                                    <tr className="nk-tb-item" key={group.id}>
                                        <td className="nk-tb-col">
                                            <Link to={`/group/${group?.id}`} className="user-card">
                                                <div className="icon mr-3 fs-5">
                                                    <em className="icon ni ni-users icons" id={`${props.id}-icon-${key}`} />
                                                </div>
                                                <div className="user-info">
                                                    <span className="tb-lead" id={`${props.id}-name-${key}`}>
                                                        {group.name}
                                                    </span>
                                                </div>
                                            </Link>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="tb-amount" id={`${props.id}-role-count-${key}`}>
                                                {group.roles?.length}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="tb-amount" id={`${props.id}-user-count-${key}`}>
                                                {group.user_count}
                                            </span>
                                        </td>
                                        <td className="nk-tb-col">
                                            <span className="text-dark" id={`${props.id}-created-at-date-${key}`}>
                                                {DateFormatter(group?.created_at)}
                                            </span>
                                            <div>
                                                <div className="meta mt-1">
                                                    <span className="version">
                                                        <span className="text-soft" id={`${props.id}-created-at-time-${key}`}>
                                                            {TimeFormatter(group?.created_at)}
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {isLoadingMore ? <GroupTableSkeletonDesktop rowCount={2} /> : null}
                            </>
                        ) : (
                            <tr>
                                <td colSpan={4}>
                                    <NoDataAvailable>
                                        <h4 id={`${props.id}-no-data`} className="text-center">{NO_GROUP_YET}</h4>
                                        <p className="text-center" id={`${props.id}-no-data-message`}>{CREATE_YOUR_GROUPS}</p>
                                    </NoDataAvailable>
                                </td>
                            </tr>
                        )
                    ) : (
                        <GroupTableSkeletonDesktop />
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default GroupTable;
